import { inject, Injectable } from '@angular/core';
import {
  DataModel,
  Factory,
  HttpFilter,
  RestService,
} from '@infodata-group/inf-commons';
import { Observable, ReplaySubject, tap } from 'rxjs';

type SavedRequest = {
  api: string;
  date: Date;
};
type SavedGetRequests = SavedRequest & {
  filters: string;
  result: Observable<unknown[]>;
};
type SavedOptionsRequests = SavedRequest & {
  result: Observable<DataModel>;
};

@Injectable({
  providedIn: 'root',
})
export class DashboardDataService {
  private storedGetRequests: SavedGetRequests[] = [];
  private storedOptionsRequests: SavedOptionsRequests[] = [];

  private readonly restService = inject(RestService);

  public getData$(
    factory: Factory<unknown>,
    filters: HttpFilter,
    forceRefresh = false,
  ): Observable<unknown[]> {
    const indexStoredResult = this.storedGetRequests.findIndex(
      (r) => r.api === factory.getApi() && r.filters === filters.toString(),
    );
    if (indexStoredResult > -1 && !forceRefresh) {
      return this.storedGetRequests[indexStoredResult].result;
    }

    const storedRequest: SavedGetRequests =
      indexStoredResult > -1
        ? this.storedGetRequests[indexStoredResult]
        : {
            api: factory.getApi(),
            filters: filters.toString(),
            result: new ReplaySubject(),
            date: new Date(),
          };
    this.storedGetRequests.push(storedRequest);
    return this.restService.getAll(factory, { httpFilter: filters }).pipe(
      tap((result) => {
        (storedRequest.result as ReplaySubject<unknown[]>).next(result);
      }),
    );
  }

  public getSchema$(api: string): Observable<DataModel> {
    const storedResult = this.storedOptionsRequests.find((r) => r.api === api);
    if (storedResult) {
      return storedResult.result;
    }
    const storedRequest: SavedOptionsRequests = {
      api,
      result: new ReplaySubject(),
      date: new Date(),
    };
    this.storedOptionsRequests.push(storedRequest);
    return this.restService.options(api).pipe(
      tap((result) => {
        (storedRequest.result as ReplaySubject<DataModel>).next(result);
      }),
    );
  }

  public clear() {
    this.storedGetRequests = [];
    this.storedOptionsRequests = [];
  }
}
